import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/react-hooks';
import { useAuth0 } from '@auth0/auth0-react';
import gql from 'graphql-tag';
import './SingleJobPage.css';
import ToolTipBarra from '../icons/TooltipBarra';
import { FaCaretDown, FaSignOutAlt } from 'react-icons/fa';
import DashCandidato from '../dashCandidato/DashboardModal';
import ChatCandidate from '../ChatCandidate';

const JOB_ACTIVE_QUERY = gql`
  query JobActive($id: ID!) {
    jobActive(id: $id) {
      id
      puesto
      rubro
      conocimientosDuros
      habilidadesBlandas
      additionalFields
      createdAt
    }
  }
`;

const JOB_ACTIVES_QUERY = gql`
  query JobActives {
    jobActives {
      id
      puesto
      createdAt
    }
  }
`;

const CANDIDATES_QUERY = gql`
  query Candidates($jobId: ID!, $username: String!) {
    correlationDISCSearch(jobId: $jobId, limit: 5, username: $username) {
      id
      name
      email
      username
      eneagrama
    }
  }
`;

const APPLIED_CANDIDATES_QUERY = gql`
  query AppliedCandidates($idJob: String!) {
    appliedCandidates(idJob: $idJob) {
      _id
      idJob
      username
      idUserlink
      name
      email
      emailOwner
      createdAt
    }
  }
`;

const SingleJobPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth0();

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const jobsPerPage = 7;
  const [highlightedCandidateId, setHighlightedCandidateId] = useState(null);
  const [isChatOpen, setIsChatOpen] = useState(false);

  // Estado para controlar qué candidato tiene el menú de tres puntitos abierto
  const [menuOpenCandidateId, setMenuOpenCandidateId] = useState(null);
  const menuRef = useRef(null);

  const username = user?.sub?.replace(/[^a-zA-Z0-9]/g, '');
  const emailOwner = user.email;

  const dropdownRef = useRef(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const handleDropdownToggle = () => setShowDropdown(!showDropdown);

  useEffect(() => {
    const handleClickOutsideDropdown = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutsideDropdown);
    return () => document.removeEventListener('mousedown', handleClickOutsideDropdown);
  }, [dropdownRef]);

  // Cerrar menú flotante de candidato al hacer clic fuera
  useEffect(() => {
    const handleClickOutsideMenu = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpenCandidateId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutsideMenu);
    return () => document.removeEventListener('mousedown', handleClickOutsideMenu);
  }, []);

  const { loading, error, data } = useQuery(JOB_ACTIVE_QUERY, {
    variables: { id },
  });
  const { loading: loadingCandidates, error: errorCandidates, data: dataCandidates } = useQuery(CANDIDATES_QUERY, {
    variables: { jobId: id, username: username },
  });
  const { loading: loadingJobs, error: errorJobs, data: dataJobs } = useQuery(JOB_ACTIVES_QUERY);
  const { 
    loading: loadingApplied, 
    error: errorApplied, 
    data: dataApplied, 
    refetch: refetchApplied 
  } = useQuery(APPLIED_CANDIDATES_QUERY, {
    variables: { idJob: data && data.jobActive ? data.jobActive.id : "" },
    skip: !(data && data.jobActive),
  });

  const leftColumnRef = useRef(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [dataApplied?.appliedCandidates]);

  if (loading) return <p className="messageJob">Cargando...</p>;
  if (error) return <p className="messageJob error">Error: {error.message}</p>;

  const job = data.jobActive;
  const additionalFields = job.additionalFields;
  const hasAdditionalFields = additionalFields && Object.keys(additionalFields).length > 0;

  const totalPages =
    dataJobs && dataJobs.jobActives
      ? Math.ceil(dataJobs.jobActives.length / jobsPerPage)
      : 0;
  const currentJobs =
    dataJobs && dataJobs.jobActives
      ? dataJobs.jobActives.slice((currentPage - 1) * jobsPerPage, currentPage * jobsPerPage)
      : [];

  const uniqueCandidates =
    dataCandidates && dataCandidates.correlationDISCSearch
      ? [...new Map(dataCandidates.correlationDISCSearch.map(candidate => [candidate.id, candidate])).values()]
      : [];

  const handleChatNavigation = () => {
    localStorage.clear();
    navigate(`/new/chat`);
  };

  const useradmin = [
    process.env.REACT_APP_ADMIN,
    process.env.REACT_APP_ADMIN2,
    process.env.REACT_APP_ADMIN3,
    process.env.REACT_APP_ADMIN4,
    process.env.REACT_APP_ADMIN5,
    process.env.REACT_APP_ADMIN6,
  ];
  const isAdmin = (currentUser) => useradmin.includes(currentUser);

  const handleCandidateApplied = (newCandidate) => {
    refetchApplied();
    setHighlightedCandidateId(newCandidate._id);
    setTimeout(() => {
      setHighlightedCandidateId(null);
    }, 3000);
  };

  // Función para abrir/cerrar el menú flotante de un candidato
  const toggleMenu = (candidateId) => {
    if (menuOpenCandidateId === candidateId) {
      setMenuOpenCandidateId(null);
    } else {
      setMenuOpenCandidateId(candidateId);
    }
  };

  return (
    <>
      <div className="pageContainer">
        {isAuthenticated && (
          <img
            src="/images/LOGO-conductualGPT-01.svg"
            className={`floating-menu-logo-silgleJob ${!sidebarOpen && 'collapsed'}`}
            alt="Logo"
          />
        )}
        {isAuthenticated && (
          <div className="profile-container">
            <div id="dropdown" ref={dropdownRef} className="dropdown">
              <img src={user.picture} alt={user.name} onClick={handleDropdownToggle} />
              {showDropdown && (
                <div className="dropdown-menu">
                  <button>
                    <a className="button-link" href="#" onClick={handleChatNavigation}>
                      Inicio
                    </a>
                  </button>
                  <button>
                    <a className="button-link" href="#" onClick={() => navigate(`/userlinkOwner`)}>
                      Gestionar Links
                    </a>
                  </button>
                  <button>
                    <a className="button-link" href="#" onClick={() => navigate(`/equipos`)}>
                      Crear equipo
                    </a>
                  </button>
                  <button>
                    <a className="button-link" href="#" onClick={() => navigate(`/createJob`)}>
                      Puesto laboral
                    </a>
                  </button>
                  <button
                    onClick={() => {
                      localStorage.clear();
                      logout({ logoutParams: { returnTo: window.location.origin } });
                    }}
                  >
                    <span>Salir</span>
                    <FaSignOutAlt />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
        <div>
          <ToolTipBarra
            sidebarOpen={sidebarOpen}
            content={sidebarOpen ? 'Cerrar barra lateral' : 'Abrir barra lateral'}
          >
            <button
              className={`sidebar-toggle ${!sidebarOpen && 'collapsed'}`}
              onClick={() => setSidebarOpen(!sidebarOpen)}
              style={{
                position: 'fixed',
                left: sidebarOpen ? '206px' : '20px',
                top: '6px',
                zIndex: 1000,
                transition: 'all 0.3s ease',
                background: '#fff',
                border: '0px solid #ddd',
                borderRadius: '50%',
                width: '33px',
                height: '33px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              {sidebarOpen ? '✕' : '☰'}
            </button>
          </ToolTipBarra>
          <div className={`sidebar ${!sidebarOpen && 'closed'}`}>
            <div className="jobVacancies">
              <h3>Candidatos</h3>
              {loadingCandidates && <p>Cargando candidatos...</p>}
              {errorCandidates && <p>Error al cargar candidatos</p>}
              {uniqueCandidates && uniqueCandidates.length > 0 && (
                <ul>
                  {uniqueCandidates.map((candidate) => (
                    <li
                      key={candidate.id}
                      className={`jobHistoryItem ${
                        selectedCandidate && selectedCandidate.id === candidate.id ? 'activeCandidate' : ''
                      }`}
                      onClick={() => setSelectedCandidate(candidate)}
                    >
                      <span>{candidate.name}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="jobVacancies">
              <h3>Vacantes Publicadas</h3>
              {loadingJobs && <p>Cargando vacantes...</p>}
              {errorJobs && <p>Error al cargar vacantes</p>}
              {dataJobs && dataJobs.jobActives && dataJobs.jobActives.length > 0 ? (
                <>
                  <ul>
                    {currentJobs.map((jobItem) => (
                      <li
                        key={jobItem.id}
                        className={`jobHistoryItem ${id === jobItem.id ? 'activeJob' : ''}`}
                      >
                        <Link
                          to={`/job/${jobItem.id}`}
                          style={{ textDecoration: 'none', color: 'inherit' }}
                        >
                          {jobItem.puesto}
                        </Link>
                      </li>
                    ))}
                  </ul>
                  <div className="pagination">
                    <button
                      onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                    >
                      «
                    </button>
                    <span>
                      De {(currentPage - 1) * jobsPerPage + 1} a{' '}
                      {Math.min(currentPage * jobsPerPage, dataJobs.jobActives.length)} - {currentPage} / {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                    >
                      »
                    </button>
                  </div>
                </>
              ) : (
                <p>No hay vacantes publicadas.</p>
              )}
            </div>
          </div>
        </div>
        <div className={`contentContainer ${!sidebarOpen && 'collapsed'}`}>
          <div className="cardsContainer">
            <div className="leftColumn" ref={leftColumnRef} style={{ overflowY: 'auto', maxHeight: '980px' }}>
              <div className="jobDetailCard">
                <h2>Detalle del Trabajo</h2>
                <div className="jobSection">
                  <h3>Puesto</h3>
                  <p>{job.puesto}</p>
                </div>
                <div className="jobSection">
                  <h3>Rubro</h3>
                  <p>{job.rubro}</p>
                </div>
                <div className="jobSection">
                  <h3>Conocimientos Duros</h3>
                  <p>{job.conocimientosDuros}</p>
                </div>
                <div className="jobSection">
                  <h3>Habilidades Blandas</h3>
                  <p>{job.habilidadesBlandas}</p>
                </div>
                {hasAdditionalFields && (
                  <div className="jobSection">
                    <h3>Información Adicional</h3>
                    {Object.entries(additionalFields).map(([key, value]) => (
                      <p key={key}>
                        <strong>{key}:</strong> {value}
                      </p>
                    ))}
                  </div>
                )}
                <div className="jobSection">
                  <h3>Creado en</h3>
                  <p>{new Date(job.createdAt).toLocaleString()}</p>
                </div>
                <div className="jobSection">
                  <h2>Candidatos aplicados</h2>
                  {loadingApplied && <p>Cargando candidatos aplicados...</p>}
                  {errorApplied && <p>Error al cargar candidatos aplicados: {errorApplied.message}</p>}
                  {dataApplied && dataApplied.appliedCandidates && dataApplied.appliedCandidates.length > 0 ? (
                    <ul>
                      {dataApplied.appliedCandidates
                        .slice()
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((candidate) => (
                          <li
                            key={candidate._id}
                            className="candidate-item"
                            style={{
                              transition: 'background-color 2s ease-out',
                              backgroundColor:
                                highlightedCandidateId === candidate._id ? 'rgb(245, 169, 56)' : 'transparent',
                              borderRadius: '5px',
                              padding: '5px',
                              position: 'relative', // Para posicionar el menú flotante
                            }}
                          >
                            <div className="candidate-info">
                              <span>{candidate.name}</span>
                              {/* Ícono de 3 puntitos que aparece al hacer hover */}
                              <div
                                className="ellipsis-menu"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleMenu(candidate._id);
                                }}
                              >
                                <span className="ellipsis-icon">⋮</span>
                              </div>
                            </div>
                            {/* Menú flotante que se muestra al hacer clic en los 3 puntitos */}
                            {menuOpenCandidateId === candidate._id && (
                              <div className="floating-menu-job" ref={menuRef}>
                                <ul>
                                  <li>Ver estado</li>
                                  <li>Eliminar</li>
                                </ul>
                              </div>
                            )}
                          </li>
                        ))}
                    </ul>
                  ) : (
                    <p>No hay candidatos aplicados.</p>
                  )}
                </div>
                <div ref={bottomRef} />
              </div>
            </div>
            <div className="rightColumn">
              <div className="candidateDetailCard">
                <h2>Candidato</h2>
                {selectedCandidate ? (
                  <div className="candidateDetail">
                    <p>
                      <strong>Nombre:</strong> {selectedCandidate.name}
                    </p>
                    <DashCandidato
                      idJob={job.id}
                      username={selectedCandidate.username}
                      idUserlink={selectedCandidate.id}
                      destinatario={selectedCandidate.name}
                      email={selectedCandidate.email}
                      emailOwner={emailOwner}
                      onCandidateApplied={handleCandidateApplied}
                      onOpenChat={() => setIsChatOpen(true)}
                    />
                  </div>
                ) : (
                  <p>Selecciona un candidato para ver los detalles.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <ChatCandidate
        destinatario={selectedCandidate ? selectedCandidate.name : ''}
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(false)}
      />
    </>
  );
};

export default SingleJobPage;
